import { useState, useEffect, useCallback } from "react";
import { APIErrorHandler, APIResponse } from "../api";

export interface UseAPIState<T> {
  data: APIResponse<T> | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  reset: () => void;
}

/**
 * useAPI Hook
 * data will be { rows: T[], count: number }
 *
 * Example:
 * const { data } = useAPI(() => menuService.getMenuItems());
 * const items = data?.rows || []; // MenuItem[]
 * const count = data?.count || 0;
 */
export function useAPI<T>(
  apiCall: () => Promise<APIResponse<T>>,
  dependencies: any[] = [],
): UseAPIState<T> {
  const [data, setData] = useState<APIResponse<T> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall();
      setData(response);
    } catch (err) {
      const errorMessage = APIErrorHandler.getErrorMessage(err);
      setError(errorMessage);
      console.error("API call failed:", errorMessage);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { data, loading, error, refetch: fetchData, reset };
}

export function useMutation<T, P = any>(
  apiCall: (params: P) => Promise<APIResponse<T>>,
) {
  const [data, setData] = useState<APIResponse<T> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (params: P): Promise<APIResponse<T> | null> => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiCall(params);
        setData(response);
        return response;
      } catch (err) {
        const errorMessage = APIErrorHandler.getErrorMessage(err);
        setError(errorMessage);
        console.error("Mutation failed:", errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiCall],
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { mutate, data, loading, error, reset };
}

export function useLazyAPI<T>(apiCall: () => Promise<APIResponse<T>>): Omit<
  UseAPIState<T>,
  "refetch"
> & {
  fetch: () => Promise<APIResponse<T> | null>;
} {
  const [data, setData] = useState<APIResponse<T> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (): Promise<APIResponse<T> | null> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall();
      setData(response);
      return response;
    } catch (err) {
      const errorMessage = APIErrorHandler.getErrorMessage(err);
      setError(errorMessage);
      console.error("Lazy API call failed:", errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { fetch, data, loading, error, reset };
}

export function usePagination<T>(
  apiCall: (page: number, perPage: number) => Promise<APIResponse<T>>,
  initialPage = 1,
  perPage = 10,
) {
  const [data, setData] = useState<APIResponse<T> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(initialPage);
  const [totalPages, setTotalPages] = useState<number>(1);

  const fetchPage = useCallback(
    async (pageNumber: number) => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiCall(pageNumber, perPage);
        setData(response);
        setTotalPages(Math.ceil(response.count / perPage));
      } catch (err) {
        const errorMessage = APIErrorHandler.getErrorMessage(err);
        setError(errorMessage);
        console.error("Pagination fetch failed:", errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [apiCall, perPage],
  );

  useEffect(() => {
    fetchPage(page);
  }, [page, fetchPage]);

  const nextPage = useCallback(() => {
    if (page < totalPages) setPage((p) => p + 1);
  }, [page, totalPages]);

  const prevPage = useCallback(() => {
    if (page > 1) setPage((p) => p - 1);
  }, [page]);

  const goToPage = useCallback(
    (pageNumber: number) => {
      if (pageNumber >= 1 && pageNumber <= totalPages) setPage(pageNumber);
    },
    [totalPages],
  );

  const refetch = useCallback(() => {
    fetchPage(page);
  }, [fetchPage, page]);

  return {
    data,
    loading,
    error,
    page,
    totalPages,
    perPage,
    nextPage,
    prevPage,
    goToPage,
    refetch,
  };
}

export function useInfiniteScroll<T>(
  apiCall: (page: number) => Promise<APIResponse<T>>,
  initialPage = 1,
) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(initialPage);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall(page);
      if (response.rows.length === 0) {
        setHasMore(false);
      } else {
        setData((prev) => [...prev, ...response.rows]);
        setPage((p) => p + 1);
      }
    } catch (err) {
      const errorMessage = APIErrorHandler.getErrorMessage(err);
      setError(errorMessage);
      console.error("Load more failed:", errorMessage);
    } finally {
      setLoading(false);
    }
  }, [apiCall, page, loading, hasMore]);

  const reset = useCallback(() => {
    setData([]);
    setPage(initialPage);
    setHasMore(true);
    setError(null);
  }, [initialPage]);

  useEffect(() => {
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error, hasMore, loadMore, reset };
}
